import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Video, FileText, StickyNote, Megaphone, ClipboardList, Play, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';
import { getLessonsForSport } from '@/components/coach/LessonPicker';
import type { Lesson } from '@/data/lessons';
import { CoachContentViewerModal } from './CoachContentViewerModal';

const SPORTS = ['football', 'soccer', 'basketball', 'baseball'] as const;
const SPORT_META: Record<string, { emoji: string; label: string }> = {
  football: { emoji: '🏈', label: 'Football' },
  soccer: { emoji: '⚽', label: 'Soccer' },
  basketball: { emoji: '🏀', label: 'Basketball' },
  baseball: { emoji: '⚾', label: 'Baseball' },
};

const TYPE_ICON = { video: Video, article: FileText, note: StickyNote } as const;
const TYPE_COLOR = { video: 'text-red-400', article: 'text-blue-400', note: 'text-yellow-400' } as const;
const TYPE_LABEL = { video: 'Video', article: 'Article', note: 'Coach Tip' } as const;
const ACCENT = '#f5a524';

type CoachContentItem = {
  id: string;
  type: 'video' | 'article' | 'note';
  title: string;
  url: string | null;
  description: string | null;
  thumbnail_url: string | null;
  sport: string;
  created_at: string;
  coach_id: string;
};

type AssignmentRow = {
  id: string;
  lesson_id: string;
  team_id: string;
  assigned_at: string;
  due_date: string | null;
};

type LessonItem = {
  kind: 'lesson';
  assignmentId: string;
  lesson: Lesson;
  sport: string;
  teamName: string;
  assignedAt: string;
  dueDate: string | null;
  done: boolean;
};

type ContentItem = {
  kind: 'content';
  content: CoachContentItem;
  unread: boolean;
  done: boolean;
};

type GroupItem = LessonItem | ContentItem;

type CoachGroup = {
  coachKey: string; // coach_profile.id (for content) or coach_user_id (for lesson-only coaches)
  coachName: string;
  items: GroupItem[];
  unreadCount: number;
  pendingLessons: number;
};

interface Props {
  onSelectLesson: (lessonId: string) => void;
}

export function CoachBanner({ onSelectLesson }: Props) {
  const { user } = useAuth();
  const { progress } = useProgress();

  const [contentRows, setContentRows] = useState<CoachContentItem[]>([]);
  const [notifContentIds, setNotifContentIds] = useState<{ id: string; content_id: string | null; is_read: boolean }[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [teamMap, setTeamMap] = useState<Map<string, string>>(new Map());
  const [teamToCoachUserId, setTeamToCoachUserId] = useState<Map<string, string>>(new Map());
  const [coachUserIdByProfileId, setCoachUserIdByProfileId] = useState<Map<string, string>>(new Map());
  const [usernameByUserId, setUsernameByUserId] = useState<Map<string, string>>(new Map());
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [viewing, setViewing] = useState<CoachContentItem | null>(null);
  // Content the CURRENT signed-in player has actually completed (server truth).
  const [viewedContentIds, setViewedContentIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!user) return;

    // Memberships → team ids → coach mapping
    const { data: memberships } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('player_user_id', user.id);
    const tIds = (memberships ?? []).map((m) => m.team_id);
    setTeamIds(tIds);

    const [contentRes, notifRes, assignRes, teamsRes, viewsRes] = await Promise.all([
      supabase.from('coach_content' as any).select('*').order('created_at', { ascending: false }).limit(40),
      supabase
        .from('player_notifications' as any)
        .select('id, content_id, is_read')
        .eq('type', 'coach_content')
        .order('created_at', { ascending: false })
        .limit(100),
      tIds.length > 0
        ? supabase
            .from('lesson_assignments')
            .select('id, lesson_id, team_id, assigned_at, due_date')
            .in('team_id', tIds)
            .order('assigned_at', { ascending: false })
        : Promise.resolve({ data: [], error: null } as any),
      tIds.length > 0
        ? supabase.from('teams').select('id, name, coach_id').in('id', tIds)
        : Promise.resolve({ data: [], error: null } as any),
      supabase
        .from('coach_content_views' as any)
        .select('content_id, viewed_at')
        .eq('player_id', user.id)
        .not('viewed_at', 'is', null),
    ]);

    const content = (!contentRes.error && contentRes.data ? (contentRes.data as any[]) : []) as CoachContentItem[];
    setContentRows(content);
    if (!notifRes.error && notifRes.data) setNotifContentIds(notifRes.data as any);
    setAssignments((assignRes.data ?? []) as any);
    setViewedContentIds(
      new Set(((!viewsRes.error && viewsRes.data ? (viewsRes.data as any[]) : []) ?? []).map((v) => v.content_id as string)),
    );

    const teams = (teamsRes.data ?? []) as any[];
    setTeamMap(new Map(teams.map((t) => [t.id, t.name])));

    // Resolve coach_profile.id → user_id for all coach refs (from content + teams)
    const coachProfileIds = Array.from(
      new Set([
        ...content.map((c) => c.coach_id).filter(Boolean),
        ...teams.map((t) => t.coach_id).filter(Boolean),
      ])
    );

    let coachMap = new Map<string, string>();
    if (coachProfileIds.length > 0) {
      const { data: coachProfiles } = await supabase
        .from('coach_profiles' as any)
        .select('id, user_id')
        .in('id', coachProfileIds);
      coachMap = new Map(((coachProfiles as any[]) ?? []).map((c) => [c.id, c.user_id]));
      // Child accounts can't read coach_profiles at all — fall back to keying
      // by the coach profile id so name resolution still has a handle.
      for (const pid of coachProfileIds) {
        if (!coachMap.has(pid)) coachMap.set(pid, pid);
      }
    }
    setCoachUserIdByProfileId(coachMap);

    // Team → coach_user_id
    const teamCoach = new Map<string, string>();
    for (const t of teams) {
      const uid = coachMap.get(t.coach_id);
      if (uid) teamCoach.set(t.id, uid);
    }
    setTeamToCoachUserId(teamCoach);

    // Resolve real coach names (full name → org → username) via a secure RPC:
    // players cannot read a coach's profile row directly, so a plain
    // `profiles` select silently returned nothing and the UI fell back to
    // the generic "Your Coach".
    if (coachProfileIds.length > 0) {
      const { data: names } = await supabase.rpc('get_coach_names' as any, {
        p_coach_ids: coachProfileIds,
      });
      const byProfileId = new Map(
        ((names as any[]) ?? []).map((n) => [n.coach_id as string, n.name as string]),
      );
      const byUserId = new Map<string, string>();
      coachMap.forEach((userId, profileId) => {
        const nm = byProfileId.get(profileId);
        if (nm) byUserId.set(userId, nm);
      });
      setUsernameByUserId(byUserId);
    } else {
      setUsernameByUserId(new Map());
    }


    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    const channel = supabase
      .channel(`coach-banner-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'coach_content' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lesson_assignments' }, () => load())
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members', filter: `player_user_id=eq.${user.id}` },
        () => load()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'player_notifications', filter: `player_id=eq.${user.id}` },
        () => load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, load]);

  const lessonIndex = useMemo(() => {
    const map = new Map<string, { lesson: Lesson; sport: string }>();
    for (const sport of SPORTS) {
      const { allLessons } = getLessonsForSport(sport);
      for (const l of allLessons) map.set(l.id, { lesson: l, sport });
    }
    return map;
  }, []);

  const unreadIds = useMemo(
    () => new Set(notifContentIds.filter((n) => !n.is_read && n.content_id).map((n) => n.content_id!)),
    [notifContentIds]
  );

  const groups: CoachGroup[] = useMemo(() => {
    const completed = new Set(progress.completedLessons ?? []);
    const byCoach = new Map<string, CoachGroup>();

    const ensure = (coachUserId: string | undefined): CoachGroup => {
      const key = coachUserId ?? 'unknown';
      let g = byCoach.get(key);
      if (!g) {
        g = {
          coachKey: key,
          coachName: (coachUserId && usernameByUserId.get(coachUserId)) || 'Your Coach',
          items: [],
          unreadCount: 0,
          pendingLessons: 0,
        };
        byCoach.set(key, g);
      }
      return g;
    };

    // Content items
    for (const c of contentRows) {
      const coachUserId = coachUserIdByProfileId.get(c.coach_id);
      const g = ensure(coachUserId);
      const done = viewedContentIds.has(c.id);
      // Once completed, it is no longer "new" to the player.
      const unread = unreadIds.has(c.id) && !done;
      g.items.push({ kind: 'content', content: c, unread, done });
      if (unread) g.unreadCount++;
      if (!done) g.pendingLessons++;
    }

    // Lesson assignments (dedupe by lesson_id per coach)
    const seenPerCoach = new Map<string, Set<string>>();
    for (const r of assignments) {
      const found = lessonIndex.get(r.lesson_id);
      if (!found) continue;
      const coachUserId = teamToCoachUserId.get(r.team_id);
      const g = ensure(coachUserId);
      const seen = seenPerCoach.get(g.coachKey) ?? new Set<string>();
      if (seen.has(r.lesson_id)) continue;
      seen.add(r.lesson_id);
      seenPerCoach.set(g.coachKey, seen);
      const done = completed.has(r.lesson_id);
      g.items.push({
        kind: 'lesson',
        assignmentId: r.id,
        lesson: found.lesson,
        sport: found.sport,
        teamName: teamMap.get(r.team_id) || 'Your team',
        assignedAt: r.assigned_at,
        dueDate: r.due_date,
        done,
      });
      if (!done) g.pendingLessons++;
    }

    // Sort items inside each group: anything still to do → completed, by recency
    for (const g of byCoach.values()) {
      g.items.sort((a, b) => {
        const aPri = a.done ? 3 : a.kind === 'lesson' ? 0 : a.unread ? 1 : 2;
        const bPri = b.done ? 3 : b.kind === 'lesson' ? 0 : b.unread ? 1 : 2;
        if (aPri !== bPri) return aPri - bPri;
        const aDate = a.kind === 'lesson' ? a.assignedAt : a.content.created_at;
        const bDate = b.kind === 'lesson' ? b.assignedAt : b.content.created_at;
        return bDate.localeCompare(aDate);
      });
    }

    return Array.from(byCoach.values()).sort(
      (a, b) => b.unreadCount + b.pendingLessons - (a.unreadCount + a.pendingLessons)
    );
  }, [contentRows, assignments, unreadIds, viewedContentIds, coachUserIdByProfileId, teamToCoachUserId, teamMap, lessonIndex, usernameByUserId, progress.completedLessons]);

  const wrapperStyle: React.CSSProperties = {
    borderLeft: `4px solid ${ACCENT}`,
    boxShadow: `0 0 0 1px rgba(245,165,36,0.08), 0 8px 24px -10px rgba(245,165,36,0.15)`,
  };

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-4 shadow-elevated" style={wrapperStyle}>
        <div className="h-5 w-40 rounded bg-white/10 animate-pulse mb-3" />
        <div className="h-12 rounded-lg bg-white/5 animate-pulse" />
      </motion.div>
    );
  }

  // Empty state (no content AND not on any team)
  if (groups.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-4 shadow-elevated relative" style={wrapperStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(245,165,36,0.15)' }}>
            <Megaphone className="w-4 h-4" style={{ color: ACCENT }} />
          </div>
          <h3 className="font-display font-bold text-sm">From Your Coach</h3>
        </div>
        <p className="text-xs font-body text-foreground/60 leading-relaxed">
          {teamIds.length === 0
            ? 'Join a team with a coach code to start receiving lessons and content here.'
            : "Your coach hasn't assigned anything yet — check back soon!"}
        </p>
      </motion.div>
    );
  }

  const markRead = async (contentId: string) => {
    const ids = notifContentIds.filter((n) => n.content_id === contentId && !n.is_read).map((n) => n.id);
    if (ids.length === 0) return;
    setNotifContentIds((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)));
    await supabase.from('player_notifications' as any).update({ is_read: true }).in('id', ids);
  };

  const handleOpenContent = (c: CoachContentItem) => {
    markRead(c.id);
    setViewing(c);
  };

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        const isExpanded = !!expanded[g.coachKey];
        const visible = isExpanded ? g.items : g.items.slice(0, 4);
        const totalAlerts = g.unreadCount + g.pendingLessons;
        return (
          <motion.div
            key={g.coachKey}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="glass-card p-4 shadow-elevated relative"
            style={wrapperStyle}
          >
            {totalAlerts > 0 && (
              <motion.span
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-display font-extrabold uppercase tracking-wider text-black shadow-md"
                style={{ backgroundColor: ACCENT }}
              >
                New
              </motion.span>
            )}

            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(245,165,36,0.18)' }}>
                  <Megaphone className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <h3 className="font-display font-bold text-sm truncate">From Coach {g.coachName}</h3>
                {totalAlerts > 0 && (
                  <span
                    className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-display font-bold flex items-center justify-center shrink-0"
                    style={{ backgroundColor: '#ef4444' }}
                  >
                    {totalAlerts}
                  </span>
                )}
              </div>
            </div>

            <p className="text-[11px] font-body text-foreground/55 mb-3">
              {g.items.length} item{g.items.length === 1 ? '' : 's'}
              {` · ${g.pendingLessons} ${g.pendingLessons === 1 ? 'item' : 'items'} to do`}
              {g.unreadCount > 0 ? ` · ${g.unreadCount} new` : ''}
            </p>

            <div className="space-y-2">
              {visible.map((it) => {
                if (it.kind === 'content') {
                  const Icon = TYPE_ICON[it.content.type];
                  return (
                    <button
                      key={`c-${it.content.id}`}
                      onClick={() => handleOpenContent(it.content)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors flex items-center gap-3 ${
                        it.done
                          ? 'border-white/5 bg-white/[0.02] opacity-70'
                          : it.unread
                            ? 'border-amber-400/40 bg-amber-400/5'
                            : 'border-white/5 hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${TYPE_COLOR[it.content.type]}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm text-foreground truncate">{it.content.title}</p>
                        <p className="text-[11px] text-foreground/50 font-body truncate">
                          {TYPE_LABEL[it.content.type]} · <span className="capitalize">{it.content.sport}</span>
                        </p>
                        {it.content.type === 'note' && it.content.description && (
                          <p className="text-xs text-foreground/70 font-body mt-1 line-clamp-2">{it.content.description}</p>
                        )}
                      </div>
                      {it.done ? (
                        <span className="flex items-center gap-1 text-[11px] font-display font-bold text-emerald-400 shrink-0">
                          <Check className="w-3.5 h-3.5" /> Done
                        </span>
                      ) : (
                        <span className="text-[11px] font-display font-bold px-2.5 py-1 rounded-md text-black shrink-0" style={{ backgroundColor: ACCENT }}>
                          View
                        </span>
                      )}
                    </button>
                  );
                }
                const meta = SPORT_META[it.sport];
                return (
                  <button
                    key={`l-${it.assignmentId}`}
                    onClick={() => onSelectLesson(it.lesson.id)}
                    className={`w-full text-left p-2.5 rounded-lg border transition-colors flex items-center gap-3 ${
                      it.done ? 'border-white/5 bg-white/[0.02] opacity-70' : 'border-primary/20 bg-primary/5 hover:bg-primary/10'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                      <ClipboardList className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm text-foreground truncate">{it.lesson.title}</p>
                      <p className="text-[11px] text-foreground/50 font-body truncate">
                        Lesson · {meta?.label ?? it.sport} · {it.teamName}
                        {it.dueDate && ` · Due ${new Date(it.dueDate).toLocaleDateString()}`}
                      </p>
                    </div>
                    {it.done ? (
                      <span className="flex items-center gap-1 text-[11px] font-display font-bold text-emerald-400 shrink-0">
                        <Check className="w-3.5 h-3.5" /> Done
                      </span>
                    ) : (
                      <span
                        className="flex items-center gap-1 text-[11px] font-display font-bold px-2.5 py-1 rounded-md text-white shrink-0"
                        style={{ backgroundColor: '#3b6ef8' }}
                      >
                        <Play className="w-3 h-3" /> Start
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {g.items.length > 4 && (
              <button
                onClick={() => setExpanded((prev) => ({ ...prev, [g.coachKey]: !prev[g.coachKey] }))}
                className="w-full text-center text-xs font-display font-bold mt-2 hover:underline"
                style={{ color: ACCENT }}
              >
                {isExpanded ? 'Show less' : `See all (${g.items.length})`}
              </button>
            )}
          </motion.div>
        );
      })}

      <CoachContentViewerModal item={viewing} onClose={() => { setViewing(null); load(); }} />
    </div>
  );
}
